import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTenant } from '../../contexts/TenantContext'
import api from '../../services/apiService'
import MemberParticipation from '../../components/client-admin/MemberParticipation'
import { MaleIcon, FemaleIcon } from '../../components/common/GenderIcon'
import '../../styles/client-admin/player-management.scss'

// Format a registration timestamp (Y-m-d H:i:s) as a readable date/year.
const formatRegisterDate = (d) => {
    if (!d) return 'N/A'
    const date = new Date(String(d).replace(' ', 'T'))
    if (isNaN(date.getTime())) return d
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

const OfficialManagement = () => {
    const { tenant } = useTenant()
    const navigate = useNavigate()
    const [currentView, setCurrentView] = useState('stateWise') // 'stateWise', 'officialList', 'officialDetail'
    const [stateWiseData, setStateWiseData] = useState([])
    const [officialsList, setOfficialsList] = useState([])
    const [officialDetail, setOfficialDetail] = useState(null)
    const [selectedState, setSelectedState] = useState('')
    const [activeTab, setActiveTab] = useState('Male') // 'Male' or 'Female' - Top level tabs
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [isEditing, setIsEditing] = useState(false)
    const [editedOfficial, setEditedOfficial] = useState(null)
    const [participation, setParticipation] = useState(null)
    const [loadingParticipation, setLoadingParticipation] = useState(false)

    // Load state-wise official count on component mount
    useEffect(() => {
        loadStateWiseData()
    }, [])

    // Handle keyboard shortcuts (ESC to go back)
    useEffect(() => {
        const handleKeyPress = (event) => {
            if (event.key === 'Escape') {
                if (currentView === 'officialDetail') {
                    handleBackToOfficialList()
                } else if (currentView === 'officialList') {
                    handleBackToStateWise()
                } else if (currentView === 'stateWise') {
                    handleBackToDashboard()
                }
            }
        }

        document.addEventListener('keydown', handleKeyPress)
        return () => document.removeEventListener('keydown', handleKeyPress)
    }, [currentView])

    const loadStateWiseData = async () => {
        setLoading(true)
        setError('')
        try {
            const response = await api.get('/client-admin/officials/state-wise-count')
            if (response.data.success) {
                setStateWiseData(response.data.data)
            } else {
                setError('Failed to load state-wise data')
            }
        } catch (err) {
            setError('Error loading state-wise data')
            console.error('Error:', err)
        } finally {
            setLoading(false)
        }
    }

    const handleViewAllOfficials = async (state) => {
        setLoading(true)
        setError('')
        setSelectedState(state)

        try {
            const response = await api.get('/client-admin/officials/by-state', {
                params: { state, gender: activeTab }
            })
            if (response.data.success) {
                setOfficialsList(response.data.data)
                setCurrentView('officialList')
            } else {
                setError('Failed to load officials list')
            }
        } catch (err) {
            setError('Error loading officials list')
            console.error('Error:', err)
        } finally {
            setLoading(false)
        }
    }

    // Load an official's tournament participation (dates/year)
    const loadParticipation = async (officialId) => {
        setParticipation(null)
        setLoadingParticipation(true)
        try {
            const res = await api.get(`/client-admin/officials/${officialId}/participation`)
            if (res.data.success) setParticipation(res.data.data)
        } catch (err) {
            console.error('Error loading participation:', err)
        } finally {
            setLoadingParticipation(false)
        }
    }

    const handleViewOfficialDetails = async (officialId) => {
        setLoading(true)
        setError('')

        try {
            const response = await api.get(`/client-admin/officials/${officialId}`)
            if (response.data.success) {
                setOfficialDetail(response.data.data)
                setCurrentView('officialDetail')
                loadParticipation(officialId)
            } else {
                setError('Failed to load official details')
            }
        } catch (err) {
            setError('Error loading official details')
            console.error('Error:', err)
        } finally {
            setLoading(false)
        }
    }

    const handleBackToStateWise = () => {
        setCurrentView('stateWise')
        setOfficialsList([])
        setSelectedState('')
    }

    const handleBackToOfficialList = () => {
        setCurrentView('officialList')
        setOfficialDetail(null)
        setIsEditing(false)
        setEditedOfficial(null)
    }

    const handleBackToDashboard = () => {
        navigate('/admin/dashboard')
    }

    const handleTabChange = async (tab) => {
        setActiveTab(tab)
        // If we're in officials list view, reload officials for new gender
        if (currentView === 'officialList' && selectedState) {
            setLoading(true)
            try {
                const response = await api.get('/client-admin/officials/by-state', {
                    params: { state: selectedState, gender: tab }
                })
                if (response.data.success) {
                    setOfficialsList(response.data.data)
                }
            } catch (err) {
                console.error('Error:', err)
            } finally {
                setLoading(false)
            }
        }
    }

    const handleAddOfficial = () => {
        navigate('/admin/officials/add') // Navigate to add official page
    }

    // Official action handlers
    const handleImageUpload = () => {
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = 'image/*'
        input.onchange = (event) => {
            const file = event.target.files[0]
            if (file) {
                const reader = new FileReader()
                reader.onload = (e) => {
                    const imageData = e.target.result
                    setOfficialDetail(prev => ({
                        ...prev,
                        image: imageData
                    }))
                    if (isEditing) {
                        setEditedOfficial(prev => ({
                            ...prev,
                            image: imageData,
                            imageFile: file
                        }))
                    }
                }
                reader.readAsDataURL(file)
            }
        }
        input.click()
    }

    const handleEditOfficial = async () => {
        if (isEditing) {
            // Save changes
            try {
                setLoading(true)

                // Map official type display text to code
                const officialTypeMap = {
                    'State Official': 'state_official',
                    'Tournament Official': 'tournament_official',
                    'Other Official': 'other_official'
                }
                const officialTypeCode = officialTypeMap[editedOfficial.officialType] || editedOfficial.officialType || ''

                // Use FormData to support file upload
                const formData = new FormData()
                formData.append('full_name', editedOfficial.fullName || '')
                formData.append('father_name', editedOfficial.fatherName || '')
                formData.append('date_of_birth', editedOfficial.dob || '')
                formData.append('gender', editedOfficial.gender === 'Male' ? 'M' : 'F')
                formData.append('state_name', editedOfficial.state || '')
                formData.append('contact', editedOfficial.contact || '')
                formData.append('email', editedOfficial.email || '')
                formData.append('official_type', officialTypeCode)
                formData.append('blood_group', editedOfficial.bloodGroup || '')
                formData.append('height', editedOfficial.height || '')
                formData.append('weight', editedOfficial.weight || '')
                formData.append('address', editedOfficial.address || '')
                formData.append('pin_code', editedOfficial.pinCode || '')
                formData.append('district', editedOfficial.district || '')
                formData.append('emergency_contact', editedOfficial.emergencyContact || '')
                formData.append('aadhar_card', editedOfficial.aadharCard || '')
                formData.append('passport_number', editedOfficial.passportNum || '')
                formData.append('marital_status', editedOfficial.maritalStatus?.toLowerCase() || '')

                // Add image file if selected
                if (editedOfficial.imageFile) {
                    formData.append('official_image', editedOfficial.imageFile)
                }

                // Use POST with _method for Laravel to handle file upload
                formData.append('_method', 'PUT')

                const response = await api.post(`/client-admin/officials/${officialDetail.id}`, formData)

                if (response.data.success) {
                    setOfficialDetail(editedOfficial)
                    setIsEditing(false)
                    alert('Official details updated successfully!')
                } else {
                    throw new Error(response.data.message || 'Failed to update official')
                }
            } catch (error) {
                console.error('Error updating official:', error)
                alert(`Failed to update official details: ${error.message}`)
            } finally {
                setLoading(false)
            }
        } else {
            // Start editing
            setEditedOfficial({...officialDetail})
            setIsEditing(true)
        }
    }

    const handleCancelEdit = () => {
        setIsEditing(false)
        setEditedOfficial(null)
    }

    const handleDeleteOfficial = async () => {
        if (window.confirm(`Are you sure you want to delete official ${officialDetail.fullName}? This action cannot be undone.`)) {
            try {
                setLoading(true)
                const response = await api.delete(`/client-admin/officials/${officialDetail.id}`)

                if (response.data.success) {
                    alert('Official deleted successfully!')
                    handleBackToOfficialList()
                } else {
                    throw new Error(response.data.message || 'Failed to delete official')
                }
            } catch (error) {
                console.error('Error deleting official:', error)
                alert('Failed to delete official. Please try again.')
            } finally {
                setLoading(false)
            }
        }
    }

    const handleBlockOfficial = async () => {
        const isBlocked = officialDetail.status === 'Blocked'
        const action = isBlocked ? 'unblock' : 'block'

        if (window.confirm(`Are you sure you want to ${action} official ${officialDetail.fullName}?`)) {
            try {
                setLoading(true)
                const response = await api.patch(`/client-admin/officials/${officialDetail.id}/status`, {
                    status: isBlocked ? 'active' : 'inactive'
                })

                if (response.data.success) {
                    setOfficialDetail(prev => ({
                        ...prev,
                        status: isBlocked ? 'Active' : 'Blocked'
                    }))
                    alert(`Official ${action}ed successfully!`)
                } else {
                    throw new Error(response.data.message || `Failed to ${action} official`)
                }
            } catch (error) {
                console.error(`Error ${action}ing official:`, error)
                alert(`Failed to ${action} official: ${error.message}`)
            } finally {
                setLoading(false)
            }
        }
    }

    const handleExportOfficials = async () => {
        try {
            setLoading(true)
            const response = await api.get('/client-admin/officials/export', {
                responseType: 'blob'
            })

            const blob = new Blob([response.data], { type: 'text/csv' })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            const date = new Date().toISOString().split('T')[0]
            link.download = `officials_data_${date}.csv`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            console.error('Export failed:', error)
            alert('Failed to export official data. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const handleFieldChange = (field, value) => {
        setEditedOfficial(prev => ({
            ...prev,
            [field]: value
        }))
    }

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '-'
        const date = new Date(dateString)
        return date.toLocaleDateString('en-IN')
    }

    // Tab component - Only shown in officials list view
    const GenderTabs = () => (
        <div className="gender-tabs" style={{ justifyContent: 'flex-start', gap: '0.5rem' }}>
            <button
                className={`tab-btn gender-tab ${activeTab === 'Male' ? 'active' : ''}`}
                onClick={() => handleTabChange('Male')}
            >
                <MaleIcon style={{ marginRight: '0.4rem' }} /> Male
            </button>
            <button
                className={`tab-btn gender-tab ${activeTab === 'Female' ? 'active' : ''}`}
                onClick={() => handleTabChange('Female')}
            >
                <FemaleIcon style={{ marginRight: '0.4rem' }} /> Female
            </button>
        </div>
    )

    // State-wise view component
    const StateWiseView = () => (
        <div className="player-management">
            <div className="page-header-banner">
                <div className="header-content">
                    <div className="header-icon">
                        <i className="fas fa-user-tie"></i>
                    </div>
                    <div className="header-text">
                        <h1>Officials Data - State wise</h1>
                        <p>Complete state-wise official statistics and management</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                    <button className="btn-export" onClick={handleAddOfficial}>
                        <i className="fas fa-user-plus"></i> Add Officials
                    </button>
                    <button
                        onClick={() => navigate('/admin/dashboard')}
                        style={{
                            background: 'linear-gradient(135deg, #c00f15 0%, #a00d12 100%)',
                            border: 'none',
                            color: 'white',
                            borderRadius: '8px',
                            padding: '10px 20px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 2px 8px rgba(192, 15, 21, 0.4)'
                        }}
                    >
                        <i className="fas fa-arrow-left"></i> Back
                    </button>
                </div>
            </div>

            <div className="management-content">
                {loading ? (
                    <div className="loading-container">
                        <i className="fas fa-spinner fa-spin"></i> Loading...
                    </div>
                ) : error ? (
                    <div className="error-container">
                        <i className="fas fa-exclamation-triangle"></i> {error}
                        <button onClick={loadStateWiseData} className="retry-btn">
                            <i className="fas fa-redo"></i> Retry
                        </button>
                    </div>
                ) : stateWiseData.length === 0 ? (
                    <div className="no-data-container">
                        <i className="fas fa-info-circle"></i>
                        <p>No officials found</p>
                    </div>
                ) : (
                    <div className="state-wise-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>S.No.</th>
                                    <th>State Name</th>
                                    <th>Total Officials</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stateWiseData.map((stateData, index) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>{stateData.state}</td>
                                        <td>{stateData.total}</td>
                                        <td>
                                            <button
                                                className="btn-view-all"
                                                onClick={() => handleViewAllOfficials(stateData.state)}
                                            >
                                                <i className="fas fa-eye"></i> View All
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    )

    // Officials list view component
    const OfficialsListView = () => (
        <div className="player-management">
            <div className="page-header-banner">
                <div className="header-content">
                    <div className="header-icon">
                        <i className="fas fa-user-tie"></i>
                    </div>
                    <div className="header-text">
                        <h1>Officials Data - {selectedState} ({activeTab})</h1>
                        <p>Total {officialsList.length} officials found</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '1rem' }}>
                    <button className="btn-export" onClick={handleAddOfficial}>
                        <i className="fas fa-user-plus"></i> Add Officials
                    </button>
                    <button className="btn-back" onClick={handleBackToStateWise} title="Back to State-wise View"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#c00f15', color: '#fff', border: 'none', padding: '0.6rem 1.25rem', borderRadius: '8px', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>
                        <i className="fas fa-arrow-left"></i> Back
                    </button>
                </div>
            </div>

            <GenderTabs />

            <div className="management-content">
                {loading ? (
                    <div className="loading-container">
                        <i className="fas fa-spinner fa-spin"></i> Loading...
                    </div>
                ) : error ? (
                    <div className="error-container">
                        <i className="fas fa-exclamation-triangle"></i> {error}
                    </div>
                ) : officialsList.length === 0 ? (
                    <div className="no-data-container">
                        <i className="fas fa-info-circle"></i>
                        <p>No officials found for {selectedState} ({activeTab})</p>
                    </div>
                ) : (
                    <div className="players-list-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>S.No.</th>
                                    <th>Official Name</th>
                                    <th>Age</th>
                                    <th>Gender</th>
                                    <th>Official Type</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {officialsList.map((official, index) => (
                                    <tr key={official.id}>
                                        <td>{index + 1}</td>
                                        <td>{official.name}</td>
                                        <td>{official.age || '-'}</td>
                                        <td>
                                            <span className={`gender-badge ${official.gender?.toLowerCase()}`}>
                                                {official.gender}
                                            </span>
                                        </td>
                                        <td>{official.official_type || '-'}</td>
                                        <td>
                                            <span className={`status-badge ${official.status?.toLowerCase()}`}>
                                                {official.status || 'Active'}
                                            </span>
                                        </td>
                                        <td>
                                            <button
                                                className="btn-view-details"
                                                onClick={() => handleViewOfficialDetails(official.id)}
                                            >
                                                <i className="fas fa-eye"></i> View Details
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    )

    // Official detail view component
    const OfficialDetailView = () => (
        <div className="player-management">
            <div className="page-header-banner">
                <div className="header-content">
                    <div className="header-icon">
                        <i className="fas fa-id-card"></i>
                    </div>
                    <div className="header-text">
                        <h1>Official ID: {officialDetail?.regId || officialDetail?.id}</h1>
                        <p>Complete official information and details</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '1rem' }}>
                    <button className="btn-back" onClick={handleBackToOfficialList} title="Back to Officials List"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#c00f15', color: '#fff', border: 'none', padding: '0.6rem 1.25rem', borderRadius: '8px', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>
                        <i className="fas fa-arrow-left"></i> Back
                    </button>
                </div>
            </div>

            <div className="management-content">
                {loading ? (
                    <div className="loading-container">
                        <i className="fas fa-spinner fa-spin"></i> Loading...
                    </div>
                ) : error ? (
                    <div className="error-container">
                        <i className="fas fa-exclamation-triangle"></i> {error}
                    </div>
                ) : officialDetail ? (
                    <div className="player-detail-form">
                        <div className="player-image-section">
                            <div className="image-placeholder">
                                {(isEditing && editedOfficial?.image) ? (
                                    <img src={editedOfficial.image} alt="Official" />
                                ) : officialDetail.image ? (
                                    <img src={officialDetail.image} alt="Official" />
                                ) : (
                                    <i className="fas fa-user-circle"></i>
                                )}
                            </div>
                            <button className="btn-upload" onClick={handleImageUpload}>
                                <i className="fas fa-camera"></i> Choose Image
                            </button>
                        </div>

                        <div className="player-details-grid">
                            <div className="detail-field">
                                <label>Name</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedOfficial?.fullName || '') : (officialDetail.fullName || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('fullName', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>Father Name</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedOfficial?.fatherName || '') : (officialDetail.fatherName || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('fatherName', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>D.O.B</label>
                                <input
                                    type="date"
                                    value={isEditing ? (editedOfficial?.dob || '') : (officialDetail.dob || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('dob', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>Gender</label>
                                {isEditing ? (
                                    <select
                                        value={editedOfficial?.gender || ''}
                                        onChange={(e) => handleFieldChange('gender', e.target.value)}
                                        className="editable"
                                    >
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                    </select>
                                ) : (
                                    <input type="text" value={officialDetail.gender || ''} disabled />
                                )}
                            </div>

                            <div className="detail-field">
                                <label>Blood Group</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedOfficial?.bloodGroup || '') : (officialDetail.bloodGroup || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('bloodGroup', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>Height</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedOfficial?.height || '') : (officialDetail.height || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('height', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>Weight</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedOfficial?.weight || '') : (officialDetail.weight || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('weight', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field full-width">
                                <label>Address</label>
                                <textarea
                                    value={isEditing ? (editedOfficial?.address || '') : (officialDetail.address || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('address', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>District</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedOfficial?.district || '') : (officialDetail.district || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('district', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>State</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedOfficial?.state || '') : (officialDetail.state || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('state', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>Post code</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedOfficial?.pinCode || '') : (officialDetail.pinCode || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('pinCode', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>Official Type</label>
                                {isEditing ? (
                                    <select
                                        value={editedOfficial?.officialType || ''}
                                        onChange={(e) => handleFieldChange('officialType', e.target.value)}
                                        className="editable"
                                    >
                                        <option value="State Official">State Official</option>
                                        <option value="Tournament Official">Tournament Official</option>
                                        <option value="Other Official">Other Official</option>
                                    </select>
                                ) : (
                                    <input type="text" value={officialDetail.officialType || ''} disabled />
                                )}
                            </div>

                            <div className="detail-field">
                                <label>Aadhar Card</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedOfficial?.aadharCard || '') : (officialDetail.aadharCard || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('aadharCard', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>Passport Number</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedOfficial?.passportNum || '') : (officialDetail.passportNum || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('passportNum', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>Marital Status</label>
                                {isEditing ? (
                                    <select
                                        value={editedOfficial?.maritalStatus || ''}
                                        onChange={(e) => handleFieldChange('maritalStatus', e.target.value)}
                                        className="editable"
                                    >
                                        <option value="">Select</option>
                                        <option value="Single">Single</option>
                                        <option value="Married">Married</option>
                                    </select>
                                ) : (
                                    <input type="text" value={officialDetail.maritalStatus || ''} disabled />
                                )}
                            </div>

                            <div className="detail-field">
                                <label>Primary Contact</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedOfficial?.contact || '') : (officialDetail.contact || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('contact', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>Secondary Contact</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedOfficial?.emergencyContact || '') : (officialDetail.emergencyContact || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('emergencyContact', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>Email</label>
                                <input
                                    type="email"
                                    value={isEditing ? (editedOfficial?.email || '') : (officialDetail.email || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('email', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field" style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '14px', background: 'linear-gradient(135deg, #fff1f1 0%, #ffe4e6 100%)', border: '1px solid #f6b8bd', borderRadius: '14px', padding: '14px 18px', marginTop: '10px', boxShadow: '0 3px 12px rgba(192,15,21,0.12)' }}>
                                <div style={{ width: '46px', height: '46px', borderRadius: '50%', background: 'linear-gradient(135deg, #e11d2a 0%, #c00f15 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 3px 8px rgba(192,15,21,0.35)' }}>
                                    <i className="fas fa-calendar-check" style={{ color: '#fff', fontSize: '1.25rem' }}></i>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '0.7rem', letterSpacing: '0.09em', textTransform: 'uppercase', color: '#c00f15', fontWeight: 700 }}>Registered On</div>
                                    <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#7a0a0e', lineHeight: 1.25 }}>{formatRegisterDate(officialDetail.createdAt)}</div>
                                </div>
                            </div>
                        </div>

                        <div className="detail-actions">
                            {isEditing ? (
                                <>
                                    <button className="btn-save" onClick={handleEditOfficial}>
                                        <i className="fas fa-save"></i> SAVE CHANGES
                                    </button>
                                    <button className="btn-cancel" onClick={handleCancelEdit}>
                                        <i className="fas fa-times"></i> CANCEL
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button className="btn-edit" onClick={handleEditOfficial}>
                                        <i className="fas fa-edit"></i> EDIT DETAILS
                                    </button>
                                    {officialDetail.status === 'Active' ? (
                                        <button
                                            style={{ background: '#6b7280', color: '#fff', border: 'none', padding: '0.5rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}
                                            onClick={async () => {
                                                if (window.confirm('Are you sure you want to deactivate this official?')) {
                                                    try {
                                                        setLoading(true)
                                                        const response = await api.patch(`/client-admin/officials/${officialDetail.id}/status`, { status: 'inactive' })
                                                        if (response.data.success) {
                                                            setOfficialDetail(prev => ({ ...prev, status: 'Inactive' }))
                                                            alert('Official deactivated successfully!')
                                                        }
                                                    } catch (error) {
                                                        alert('Failed to deactivate official: ' + error.message)
                                                    } finally { setLoading(false) }
                                                }
                                            }}
                                        >
                                            <i className="fas fa-toggle-off"></i> DEACTIVATE
                                        </button>
                                    ) : officialDetail.status !== 'Blocked' ? (
                                        <button
                                            style={{ background: '#6b7280', color: '#fff', border: 'none', padding: '0.5rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}
                                            onClick={async () => {
                                                if (window.confirm('Are you sure you want to activate this official?')) {
                                                    try {
                                                        setLoading(true)
                                                        const response = await api.patch(`/client-admin/officials/${officialDetail.id}/status`, { status: 'active' })
                                                        if (response.data.success) {
                                                            setOfficialDetail(prev => ({ ...prev, status: 'Active' }))
                                                            alert('Official activated successfully!')
                                                        }
                                                    } catch (error) {
                                                        alert('Failed to activate official: ' + error.message)
                                                    } finally { setLoading(false) }
                                                }
                                            }}
                                        >
                                            <i className="fas fa-toggle-on"></i> ACTIVATE
                                        </button>
                                    ) : null}
                                    <button
                                        className={officialDetail.status === 'Blocked' ? 'btn-unblock' : 'btn-block'}
                                        onClick={handleBlockOfficial}
                                        style={{ width: 'auto', maxWidth: '220px', padding: '0.5rem 1.5rem', fontSize: '0.875rem' }}
                                    >
                                        <i className={officialDetail.status === 'Blocked' ? 'fas fa-unlock' : 'fas fa-ban'}></i>
                                        {officialDetail.status === 'Blocked' ? 'UNBLOCK OFFICIAL' : 'BLOCK OFFICIAL'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    )

    // Render current view
    const renderCurrentView = () => {
        switch (currentView) {
            case 'stateWise':
                return <StateWiseView />
            case 'officialList':
                return <OfficialsListView />
            case 'officialDetail':
                return <OfficialDetailView />
            default:
                return <StateWiseView />
        }
    }

    return renderCurrentView()
}

export default OfficialManagement
